import { Fragment, type ReactElement, useContext, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import CheckIcon from "@mui/icons-material/Check"
import ClearIcon from "@mui/icons-material/Clear"
import CloseIcon from "@mui/icons-material/Close"
import EditIcon from "@mui/icons-material/Edit"
import { Grid, Link } from "@mui/material"
import {
  Button,
  type InputChangeEvent,
  InputText,
  NotificationUtils,
  Tag,
  Tooltip,
  Typography,
} from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { JsonPopper } from "../../../../components/data-display/JsonPopper"
import { KonanAPI } from "../../../../services/KonanAPI"
import { queryClient } from "../../../../store/QueryClientStore"
import type { RootState } from "../../../../store/ReduxStore"
import type { BulkCreateFeedbackRequest } from "../../../../types/generated/api/BulkCreateFeedbackRequest"
import type { BulkCreateFeedbackResponse } from "../../../../types/generated/api/BulkCreateFeedbackResponse"
import type { PatchedUpdateFeedbackRequest } from "../../../../types/generated/api/PatchedUpdateFeedbackRequest"
import type { UpdateFeedback } from "../../../../types/generated/api/UpdateFeedback"
import { DecisionsContext } from "../../context/Context"
import type { FeatureKeyWithTooltipProps, RenderCellProps } from "../../interfaces"
import { decisionReason } from "../../utils/helpers"
import { ActualDecisionSelector } from "../Elements/ActualDecisionSelector"

import styles from "./RequestBreakDownTable.module.scss"

/**
 * Shows the last segment of a dotted key with a tooltip for the full key.
 */
function FeatureKeyWithTooltip({ fullKey }: FeatureKeyWithTooltipProps): JSX.Element {
  const segments = fullKey.split(".")
  const last = segments[segments.length - 1] ?? fullKey
  return (
    <Tooltip title={fullKey} placement="top">
      <Typography variant="p" style={{ cursor: "help" }}>
        {last}
      </Typography>
    </Tooltip>
  )
}

/**
 * Renders a value with special handling for JSON-like objects via popover.
 */
function RenderCell({ value }: RenderCellProps): ReactElement {
  const { t } = useTranslation("decisions")
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  if (Boolean(value) && (typeof value === "object" || (Array.isArray(value) && value.length > 0))) {
    return (
      <Fragment>
        {anchorEl && (
          <JsonPopper
            id="popper-id"
            isOpen={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorEl={anchorEl}
            content={value as Record<string, unknown>}
          />
        )}
        <Link onClick={(e) => setAnchorEl(e.target as HTMLButtonElement | null)} className={styles.viewLink}>
          {t("requestBreakDown.view")}
        </Link>
      </Fragment>
    )
  }

  // boolean
  if (typeof value === "boolean") {
    return value ? <CheckIcon className={styles.checkIcon} /> : <CloseIcon className={styles.closeIcon} />
  }

  // empty array
  if (Array.isArray(value) && value.length === 0)
    return (
      <Typography noWrap variant="p">
        __
      </Typography>
    )

  // null/undefined
  if (value === null || value === undefined)
    return (
      <Typography noWrap variant="p">
        __
      </Typography>
    )

  // default
  return (
    <Typography noWrap variant="p">
      {String(value)}
    </Typography>
  )
}

function FeedbackCell({
  feedback,
  onSubmit,
  isLoading,
}: {
  feedback: Record<string, unknown> | null
  onSubmit: (feedback: Record<string, unknown> | string) => Promise<void>
  isLoading: boolean
}): ReactElement {
  const [mode, setMode] = useState<"view" | "edit">("view")
  const [feedbackValue, setFeedbackValue] = useState<string | undefined>(feedback?.toString())

  return (
    <Grid container gap={0.5}>
      <Grid item xs width={"calc(100% - 72px)"}>
        {mode === "view" ? (
          <RenderCell value={feedback} />
        ) : (
          <InputText
            value={feedbackValue}
            handleChange={(e: InputChangeEvent) => {
              setFeedbackValue(e.target.value as string)
            }}
            fullWidth
            hideDescription
            size="small"
          />
        )}
      </Grid>

      {mode === "view" ? (
        <Grid item>
          <Button
            size="small"
            onClick={() => {
              setMode("edit")
            }}
          >
            <EditIcon fontSize="inherit" style={{ fill: "var(--blue-background-1)", fontSize: "16px" }} />
          </Button>
        </Grid>
      ) : (
        <Fragment>
          <Grid item>
            <Button
              size="small"
              onClick={async () => {
                await onSubmit(feedbackValue as string)
                setMode("view")
              }}
              disabled={!feedbackValue || feedbackValue === feedback?.toString()}
              isLoading={isLoading}
            >
              <CheckIcon fontSize="inherit" style={{ fill: "var(--green-background-1)", fontSize: "16px" }} />
            </Button>
          </Grid>
          <Grid item>
            <Button
              size="small"
              onClick={() => {
                setMode("view")
              }}
            >
              <ClearIcon fontSize="inherit" style={{ fill: "var(--red-background-1)", fontSize: "16px" }} />
            </Button>
          </Grid>
        </Fragment>
      )}
    </Grid>
  )
}

/**
 * Details table for decision, input features, and calculated features.
 */
export function RequestBreakDown(): ReactElement {
  const { labels, selectedRow, hasNextLabelsPage, fetchNextLabelsPage, setLabelsSearchValue } =
    useContext(DecisionsContext)
  const { id: projectId } = useParams<{ id: string }>()
  const currentProject = useSelector((state: RootState) => state.project.currentProject)
  const { t } = useTranslation("decisions")

  const current = selectedRow

  const createFeedbackMutation = useMutation<
    AxiosResponse<BulkCreateFeedbackResponse>,
    AxiosError,
    BulkCreateFeedbackRequest & { project_uuid: string }
  >(KonanAPI.createFeedback, {
    onSuccess: () => {
      queryClient.invalidateQueries(["project-predictions"])
      NotificationUtils.toast(t("projectDecisions.notifications.feedbackSubmitted"), {
        severity: "success",
      })
    },
    onError: () => {
      queryClient.invalidateQueries(["project-predictions"])
      NotificationUtils.toast(t("projectDecisions.notifications.feedbackSubmissionFailed"), {
        severity: "error",
      })
    },
  })

  const updateFeedbackMutation = useMutation<
    AxiosResponse<UpdateFeedback>,
    AxiosError,
    { prediction_uuid: string } & PatchedUpdateFeedbackRequest
  >(KonanAPI.updateFeedback, {
    onSuccess: () => {
      queryClient.invalidateQueries(["project-predictions"])

      NotificationUtils.toast(t("projectDecisions.notifications.feedbackSubmitted"), {
        severity: "success",
      })
    },
    onError: () =>
      NotificationUtils.toast(t("projectDecisions.notifications.feedbackSubmissionFailed"), {
        severity: "error",
      }),
  })

  if (!current) return <></>

  const decision = current.joint_output
  const feedback = current.feedback

  // helper functions
  const parseFeatures = (): Record<string, unknown> => {
    try {
      return current.features ? JSON.parse(current.features) : {}
    } catch {
      return {}
    }
  }

  const renderReason = (): React.ReactNode => {
    const reasonData = decisionReason(current, {
      category: currentProject?.category,
      aiEngineLabel: t("projectDecisions.aiEngine"),
    })

    if (reasonData.type === "__") {
      return <Typography variant="p">{reasonData.type}</Typography>
    }

    return (
      <Tooltip title={reasonData.tooltip}>
        <Tag size="small" color="blue" variant="filled">
          {reasonData.type ?? t("reasonNotSpecified")}
        </Tag>
      </Tooltip>
    )
  }

  const feedbackSubmitHandler = async (value: Record<string, unknown> | string): Promise<void> => {
    if (!feedback) {
      await createFeedbackMutation.mutateAsync({
        project_uuid: projectId as string,
        feedback: [{ prediction_uuid: current.uuid, target: value as Record<string, unknown> }],
      })
    } else {
      await updateFeedbackMutation.mutateAsync({
        prediction_uuid: current.uuid,
        target: value as Record<string, unknown>,
      })
    }
  }

  const features: Record<string, unknown> = current.features_json ?? parseFeatures()

  const calculatedFeatures: Record<string, unknown> = current.workflow_output?.calculated_features ?? {}

  return (
    <div className={styles.tableCard}>
      {/* Decision table */}
      <table className={`${styles.sectionTable} ${styles.decision}`}>
        <thead>
          <tr>
            <th colSpan={2} className={styles.header1}>
              {t("requestBreakDown.decision")}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className={styles.decisionLabel}>{t("requestBreakDown.decision")}</td>
            <td>
              <RenderCell value={decision} />
            </td>
          </tr>
          <tr>
            <td className={styles.decisionLabel}>{t("requestBreakDown.reason")}</td>
            <td>{renderReason()}</td>
          </tr>
          <tr>
            <td className={styles.decisionLabel}>{t("requestBreakDown.actualDecision")}</td>
            <td className={styles.actualDecisionCell}>
              <ActualDecisionSelector
                projectId={projectId}
                current={current}
                labels={labels}
                hasNextLabelsPage={hasNextLabelsPage}
                fetchNextLabelsPage={fetchNextLabelsPage}
                setLabelsSearchValue={setLabelsSearchValue}
              />
            </td>
          </tr>
          <tr>
            <td className={styles.decisionLabel}>{t("requestBreakDown.feedback")}</td>
            <td>
              <FeedbackCell
                feedback={feedback}
                onSubmit={feedbackSubmitHandler}
                isLoading={createFeedbackMutation.isLoading || updateFeedbackMutation.isLoading}
              />
            </td>
          </tr>
        </tbody>
      </table>

      {/* Input features */}
      <table className={`${styles.sectionTable} ${styles.input}`}>
        <thead>
          <tr>
            <th colSpan={2} className={styles.headerFeatures}>
              {t("requestBreakDown.inputFeatures")}
            </th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(features).length === 0 ? (
            <tr>
              <td colSpan={2} className={styles.emptyCell}>
                {t("requestBreakDown.noInputFeatures")}
              </td>
            </tr>
          ) : (
            Object.entries(features).map(([key, value]) => (
              <tr key={key}>
                <td className={styles.labelFeatures}>
                  <FeatureKeyWithTooltip fullKey={key} />
                </td>
                <td>
                  <RenderCell value={value} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Calculated features */}
      {currentProject?.category === "workflow" && (
        <table className={`${styles.sectionTable} ${styles.calculated}`}>
          <thead>
            <tr>
              <th colSpan={2} className={styles.headerCalculated}>
                {t("requestBreakDown.calculatedFeatures")}
              </th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(calculatedFeatures).length === 0 ? (
              <tr>
                <td colSpan={2} className={styles.emptyCell}>
                  {t("requestBreakDown.noCalculatedFeatures")}
                </td>
              </tr>
            ) : (
              Object.entries(calculatedFeatures).map(([key, value]) => (
                <tr key={key}>
                  <td className={styles.labelFeatures}>
                    <FeatureKeyWithTooltip fullKey={key} />
                  </td>
                  <td>
                    <RenderCell value={value} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  )
}
